import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PenTool, Users, BookOpen, Sparkles, ScrollText, Wand2, Quote } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";

const HERO_IMG = "https://images.unsplash.com/photo-1773606381964-11a82a2e17fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxmb3VudGFpbiUyMHBlbiUyMHBhcmNobWVudCUyMGRlc2t8ZW58MHx8fHwxNzc4MjEyNjYzfDA&ixlib=rb-4.1.0&q=85";
const INK_IMG = "https://images.unsplash.com/photo-1498721409281-998093cc905b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxpbmslMjBkcm9wcGluZyUyMGFic3RyYWN0JTIwYXJ0fGVufDB8fHx8MTc3ODIxMjY2M3ww&ixlib=rb-4.1.0&q=85";
const TYPEWRITER_IMG = "https://images.unsplash.com/photo-1767436620079-c423be9f9a69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwdHlwZXdyaXRlciUyMHdyaXRpbmclMjBkZXNrfGVufDB8fHx8MTc3ODIxMjY1OHww&ixlib=rb-4.1.0&q=85";
const LIBRARY_IMG = "https://images.unsplash.com/photo-1698434156111-891f3a1261d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbGlicmFyeSUyMHZpbnRhZ2UlMjBib29rc2hlbGZ8ZW58MHx8fHwxNzc4MjEyNjYzfDA&ixlib=rb-4.1.0&q=85";

const GENRES = [
  { name: "Mystery", note: "drive on a question" },
  { name: "Thriller", note: "pressure & threat" },
  { name: "Drama", note: "internal conflict" },
  { name: "Romance", note: "feelings forward" },
  { name: "Sci-Fi", note: "world rules matter" },
  { name: "Fantasy", note: "built-world magic" },
  { name: "Horror", note: "dread accumulates" },
  { name: "Literary", note: "voice over plot" },
  { name: "Comedy", note: "wry, character-rooted" },
  { name: "Open", note: "no constraint" },
];

const Landing = () => {
  return (
    <div className="min-h-screen bg-stone-950 grain-overlay" data-testid="landing-page">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      {/* ─── HERO ─── Asymmetric split: left text, right image */}
      <section className="relative pt-24 sm:pt-28 pb-20 sm:pb-32 px-6 sm:px-10 lg:px-16">
        <div className="absolute inset-0 bg-gradient-to-b from-rose-950/20 via-stone-950/40 to-stone-950 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left: copy + CTAs */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-6">
              <PenTool className="w-4 h-4 text-rose-800" strokeWidth={1.5} />
              <span className="font-sans-ui text-xs uppercase tracking-[0.3em] text-stone-400">Inkwell &mdash; a writing studio</span>
            </div>
            <h1 className="font-serif-heading text-5xl sm:text-6xl lg:text-7xl text-stone-100 leading-[1.05] tracking-tight">
              Write fiction <span className="text-rose-400 italic">together</span><br />
              with characters who <em className="text-stone-50">remember.</em>
            </h1>
            <p className="font-serif-reading text-stone-400 text-xl mt-8 max-w-xl leading-relaxed">
              A studio for collaborative storytelling. Build your cast, set the genre, and let
              the prose form between your hands and theirs &mdash; chapter by chapter, in your own voice.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-12">
              <Link to="/auth" data-testid="hero-begin-writing-cta">
                <button className="group inline-flex items-center gap-3 px-8 py-4 bg-rose-800 hover:bg-rose-700 text-rose-50 font-sans-ui text-xs uppercase tracking-[0.3em] transition-all hover:-translate-y-0.5">
                  Begin writing
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                </button>
              </Link>
              <Link to="/auth" data-testid="hero-signin-cta">
                <button className="inline-flex items-center gap-2 px-7 py-4 border border-stone-700 hover:border-stone-500 text-stone-300 hover:text-stone-100 font-sans-ui text-xs uppercase tracking-[0.3em] transition">
                  Sign in
                </button>
              </Link>
            </div>
            <div className="hairline mt-14 max-w-md" />
            <div className="font-serif-reading italic text-stone-500 text-sm mt-6 max-w-md leading-relaxed">
              <span className="text-rose-400 not-italic font-sans-ui text-[10px] uppercase tracking-[0.25em] mr-2">All genres &mdash;</span>
              including the daring ones. You set the tone.
            </div>
          </div>

          {/* Right: hero image w/ glassmorphism overlay */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden border border-stone-800/60">
              <img src={HERO_IMG} alt="Fountain pen on parchment" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-stone-950/70 via-stone-950/20 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 backdrop-blur-md bg-stone-950/40 border border-stone-700/40 p-5">
                <Quote className="w-4 h-4 text-rose-400 mb-2" strokeWidth={1.5} />
                <p className="font-serif-reading italic text-stone-100 text-sm leading-relaxed">
                  &ldquo;Every chapter forms in front of you. You guide it, you keep it, you shape what comes next.&rdquo;
                </p>
              </div>
            </div>
            <div className="absolute -top-3 -left-3 w-24 h-24 border border-rose-900/40 -z-10" />
            <div className="absolute -bottom-3 -right-3 w-32 h-32 border border-stone-700/40 -z-10" />
          </div>
        </div>
      </section>

      {/* ─── AI CHARACTERS ─── Overlapping dossier cards */}
      <section className="relative py-20 sm:py-28 px-6 sm:px-10 lg:px-16 border-t border-stone-800/40 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <img src={INK_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Users className="w-5 h-5 text-rose-400 mb-4" strokeWidth={1.5} />
            <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-3">The Cast</div>
            <h2 className="font-serif-heading text-4xl sm:text-5xl text-stone-100 leading-[1.1]">
              Characters with <span className="italic text-rose-300">memory.</span>
            </h2>
            <p className="font-serif-reading text-stone-400 text-lg mt-6 leading-relaxed">
              Choose from a curated cast or compose your own. Inkwell holds the canon for you &mdash;
              every chapter quietly updates a story bible the AI honors. They remember what was
              said, who saw what, who lied, who left.
            </p>
            <Link to="/auth" data-testid="characters-section-cta" className="inline-flex items-center gap-2 mt-8 font-sans-ui text-xs uppercase tracking-[0.3em] text-rose-400 hover:text-rose-300 transition">
              Meet them <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="lg:col-span-7 relative min-h-[400px]">
            {/* Dossier 1 */}
            <div className="relative lg:absolute lg:top-0 lg:left-0 lg:w-[78%] mb-6 lg:mb-0 bg-stone-900/80 backdrop-blur border border-stone-700/60 p-7 hover:-translate-y-1 transition-transform z-10">
              <div className="flex items-baseline justify-between mb-4">
                <span className="font-serif-heading text-2xl text-stone-100">Marcus Rey</span>
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500">Dossier &middot; 01</span>
              </div>
              <p className="font-serif-reading italic text-stone-300 text-base leading-relaxed">
                A jazz pianist who reads people like sheet music &mdash; every flaw a note he can play.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                {["patient", "observant", "magnetic"].map(t => (
                  <span key={t} className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-rose-200 bg-rose-950/30 border border-rose-900/40 px-2 py-1">{t}</span>
                ))}
              </div>
            </div>
            {/* Dossier 2 */}
            <div className="relative lg:absolute lg:top-44 lg:left-32 lg:w-[78%] mb-6 lg:mb-0 bg-stone-900/80 backdrop-blur border border-stone-700/60 p-7 hover:-translate-y-1 transition-transform z-20">
              <div className="flex items-baseline justify-between mb-4">
                <span className="font-serif-heading text-2xl text-stone-100">Ines Caldera</span>
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500">Dossier &middot; 02</span>
              </div>
              <p className="font-serif-reading italic text-stone-300 text-base leading-relaxed">
                A Lisbon novelist running from her last manuscript and the lover it exposed.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                {["sharp-tongued", "reckless", "tender"].map(t => (
                  <span key={t} className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-rose-200 bg-rose-950/30 border border-rose-900/40 px-2 py-1">{t}</span>
                ))}
              </div>
            </div>
            {/* Or your own */}
            <div className="relative lg:absolute lg:top-[22rem] lg:left-0 lg:w-[60%] bg-stone-950/80 backdrop-blur border border-rose-900/40 p-6 hover:-translate-y-1 transition-transform z-30">
              <Sparkles className="w-4 h-4 text-rose-400 mb-2" strokeWidth={1.5} />
              <div className="font-serif-heading text-xl text-stone-100 mb-1">Or compose your own.</div>
              <p className="font-serif-reading italic text-stone-400 text-sm leading-relaxed">
                Name them, give them traits, a description. They become part of the cast.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WRITING EXPERIENCE ─── Control-room grid */}
      <section className="py-20 sm:py-28 px-6 sm:px-10 lg:px-16 border-t border-stone-800/40">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <ScrollText className="w-5 h-5 text-rose-400 mb-4" strokeWidth={1.5} />
            <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-3">The Studio</div>
            <h2 className="font-serif-heading text-4xl sm:text-5xl text-stone-100 leading-[1.1]">
              The page is <span className="italic text-rose-300">alive</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-800/40">
            {[
              { icon: PenTool, title: "Typewriter streaming", body: "Words form in front of you, one at a time. You can stop, redirect, rewrite at any beat." },
              { icon: BookOpen, title: "Story bible", body: "A living canon updates after every chapter. Edit it directly &mdash; the AI honors what's there." },
              { icon: Wand2, title: "Genre lanes", body: "Mystery, thriller, drama, sci-fi, romance, more. Each lane changes how the AI moves through scenes." },
              { icon: Users, title: "Texting & prose", body: "Switch between cinematic prose and SMS-style scenes &mdash; in the same story, mid-chapter." },
              { icon: Sparkles, title: "Voice", body: "Hear your characters speak in voices you choose. Optional &mdash; tap any line to play it back." },
              { icon: ScrollText, title: "Yours to keep", body: "Download chapters as PDF, Markdown, or plain text. Format-aware. The text is yours." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-stone-950 p-8 hover:bg-stone-900/60 transition-colors group">
                <Icon className="w-5 h-5 text-rose-400 mb-5" strokeWidth={1.5} />
                <div className="font-serif-heading text-xl text-stone-100 mb-3">{title}</div>
                <p className="font-serif-reading text-stone-400 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── GENRES ─── Editorial grid */}
      <section className="relative py-20 sm:py-28 px-6 sm:px-10 lg:px-16 border-t border-stone-800/40 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
          <img src={LIBRARY_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <BookOpen className="w-5 h-5 text-rose-400 mb-4" strokeWidth={1.5} />
            <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-3">The Lanes</div>
            <h2 className="font-serif-heading text-4xl sm:text-5xl text-stone-100 leading-[1.1]">
              Pick a lane. <span className="italic text-rose-300">Or don&apos;t.</span>
            </h2>
            <p className="font-serif-reading text-stone-400 text-lg mt-6 leading-relaxed">
              Choose a genre and the AI follows it &mdash; or leave it open and let the story tell you what it is.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {GENRES.map((g, i) => (
              <div
                key={g.name}
                className="bg-stone-900/60 backdrop-blur border border-stone-800/60 hover:border-rose-900/50 hover:-translate-y-0.5 transition p-5"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div className="font-serif-heading text-xl text-stone-100">{g.name}</div>
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.22em] text-stone-500 mt-2">{g.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── Massive padding, subtle disclaimer */}
      <footer className="relative py-24 px-6 sm:px-10 lg:px-16 border-t border-stone-800/40">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-end">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <PenTool className="w-4 h-4 text-rose-800" strokeWidth={1.5} />
              <span className="font-serif-heading text-2xl text-stone-100">Inkwell</span>
            </div>
            <p className="font-serif-reading italic text-stone-400 text-base max-w-md leading-relaxed">
              A writing studio for stories that won&apos;t write themselves.
            </p>
          </div>
          <div className="md:text-right">
            <Link to="/auth" data-testid="footer-begin-cta">
              <button className="inline-flex items-center gap-3 px-8 py-3.5 bg-rose-800 hover:bg-rose-700 text-rose-50 font-sans-ui text-xs uppercase tracking-[0.3em] transition">
                Begin writing
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </Link>
            <div className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-600 mt-6">
              For mature readers &middot; 18+
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
